import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/utils/cn";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Rooms", href: "/rooms" },
  { label: "Dining", href: "/dining" },
  { label: "Experiences", href: "/experiences" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export default function PublicHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setScrolled(window.scrollY > 40);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleBookNow = () => {
    navigate("/book");
    setMobileOpen(false);
  };

  const isSolid = !isHome || scrolled;

  return (
    <>
      {isHome && (
        <div className="hidden bg-metro-green-950 text-xs text-white md:block">
          <div className="mx-auto flex max-w-container items-center justify-between px-6 py-2 md:px-8">
            <span className="flex items-center gap-1.5">
              <MapPin size={12} />
              Boulevard Road, Dal Lake, Srinagar 190001
            </span>
            <div className="flex items-center gap-6">
              <a
                href="tel:+919906510627"
                className="flex items-center gap-1.5 hover:opacity-80"
              >
                <Phone size={12} />
                +91 99065 10627
              </a>
              <a
                href="mailto:metropoliskmr1@gmail.com"
                className="flex items-center gap-1.5 hover:opacity-80"
              >
                <Mail size={12} />
                metropoliskmr1@gmail.com
              </a>
            </div>
          </div>
        </div>
      )}

      <header
        className={cn(
          "sticky top-0 z-50 transition-all duration-300",
          isSolid ? "bg-white shadow-sm" : "bg-transparent",
        )}
      >
        <div className="mx-auto flex max-w-container items-center justify-between px-6 py-4 md:px-8">
          <Link
            to="/"
            className="font-serif text-2xl font-semibold tracking-widest-x text-metro-green-900"
          >
            METROPOLIS
          </Link>

          <nav className="hidden items-center gap-8 text-sm font-medium text-metro-ink lg:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "transition hover:opacity-60",
                  pathname === link.href && "text-metro-green-800",
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <select
              className="hidden cursor-pointer border-none bg-transparent text-sm text-metro-ink outline-none md:block"
              aria-label="Language"
              defaultValue="en"
            >
              <option value="en">EN</option>
              <option value="hi">HI</option>
            </select>
            <Button
              size="sm"
              className="hidden md:inline-flex"
              onClick={handleBookNow}
            >
              Book Now
            </Button>
            <button
              type="button"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} className="text-metro-green-900" />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-white">
          <div className="flex items-center justify-between border-b border-metro-cream-100 px-6 py-4">
            <span className="font-serif text-xl font-semibold tracking-widest-x text-metro-green-900">
              METROPOLIS
            </span>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col gap-6 p-6 font-serif text-lg text-metro-green-900">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={cn(pathname === link.href && "text-metro-green-600")}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="mt-auto border-t border-metro-cream-100 p-6 space-y-4">
            <div className="flex flex-col gap-2 text-sm text-metro-ink-muted">
              <a
                href="tel:+919906510627"
                className="flex items-center gap-2 hover:text-metro-green-800"
              >
                <Phone size={14} />
                +91 99065 10627
              </a>
              <a
                href="mailto:metropoliskmr1@gmail.com"
                className="flex items-center gap-2 hover:text-metro-green-800"
              >
                <Mail size={14} />
                metropoliskmr1@gmail.com
              </a>
            </div>
            <Button fullWidth onClick={handleBookNow}>
              Book Now
            </Button>
          </div>
        </div>
      )}
    </>
  );
}